/**
 * Phase 178 — Autonomous Decision Health Dashboard + Phase 168.e Funnel
 *
 * Unified read-only ledger of every autonomous decision the platform made
 * in the last N hours across:
 *   • Phase 167  — weekly scout digest sends
 *   • Phase 169  — Auto-Pilot manual fires
 *   • Phase 170  — signal-triggered fires
 *
 * Each row exposes a one-click Revert that delegates to the canonical
 * announcement-delete handler so retraction stays consistent platform-wide.
 *
 * The Phase 168.e funnel chart (sent → delivered → opened → clicked → landed)
 * is rendered inline as the top tile.
 */
import { useCallback, useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Activity, Loader2, RefreshCw, Mail, Rocket, Satellite,
  Send, CheckCircle2, MailOpen, MousePointerClick, MapPin, Undo2,
  Sparkles, FileText,
} from 'lucide-react';
import { toast } from 'sonner';

const SOURCE_META = {
  phase167_digest: {
    label: 'Phase 167 · Weekly Digest',
    icon: Mail,
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconColor: 'text-emerald-600',
    bg: 'bg-emerald-50',
  },
  phase169_autopilot: {
    label: 'Phase 169 · Auto-Pilot',
    icon: Rocket,
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    iconColor: 'text-amber-600',
    bg: 'bg-amber-50',
  },
  phase170_signal: {
    label: 'Phase 170 · External Signal',
    icon: Satellite,
    color: 'bg-sky-100 text-sky-800 border-sky-200',
    iconColor: 'text-sky-600',
    bg: 'bg-sky-50',
  },
};

const STATUS_PILL = {
  sent:              'bg-emerald-50 text-emerald-700 border-emerald-200',
  dispatched:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  queued:            'bg-blue-50 text-blue-700 border-blue-200',
  failed:            'bg-rose-50 text-rose-700 border-rose-200',
  deduped:           'bg-amber-50 text-amber-700 border-amber-200',
  ignored_no_match:  'bg-slate-50 text-slate-600 border-slate-200',
  dry_run:           'bg-violet-50 text-violet-700 border-violet-200',
  reverted:          'bg-rose-50 text-rose-700 border-rose-200',
};

// Funnel chart inline component — uses tabular-nums + smooth tapering bars
function FunnelChart({ stages, bounceRate }) {
  const maxCount = stages?.[0]?.count || 1;
  return (
    <div className="space-y-2.5" data-testid="phase168e-funnel">
      {(stages || []).map((s, idx) => {
        const widthPct = Math.max(8, (s.count / maxCount) * 100);
        const colors = [
          'bg-gradient-to-r from-slate-500 to-slate-700',     // sent
          'bg-gradient-to-r from-emerald-500 to-emerald-700', // delivered
          'bg-gradient-to-r from-blue-500 to-blue-700',       // opened
          'bg-gradient-to-r from-violet-500 to-violet-700',   // clicked
          'bg-gradient-to-r from-amber-500 to-amber-700',     // landed
        ];
        const icons = [Send, CheckCircle2, MailOpen, MousePointerClick, MapPin];
        const Icon = icons[idx] || Send;
        return (
          <div key={s.key} className="flex items-center gap-2"
            data-testid={`phase168e-stage-${s.key}`}>
            <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 w-[64px] shrink-0">
              {s.label}
            </span>
            <div className="flex-1 bg-slate-100 rounded-full overflow-hidden h-7 relative">
              <div
                className={`h-full ${colors[idx] || colors[0]} rounded-full transition-all duration-300`}
                style={{ width: `${widthPct}%` }}
              />
              <div className="absolute inset-0 flex items-center px-2.5 text-xs font-extrabold text-white drop-shadow-sm tabular-nums">
                {s.count.toLocaleString('en-IN')}
              </div>
            </div>
            <span className="text-[11px] font-bold text-slate-700 w-[56px] text-right tabular-nums">
              {s.pct_of_sent}%
            </span>
          </div>
        );
      })}
      {bounceRate !== undefined && (
        <p className="text-[10px] text-slate-400 mt-1">
          Bounce rate: <strong className="text-slate-700">{bounceRate}%</strong>
        </p>
      )}
    </div>
  );
}

export default function AutonomousLedger() {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState(null);
  const [funnelLogType, setFunnelLogType] = useState('*');
  const [funnelDays, setFunnelDays] = useState(7);
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/autonomous/ledger?hours=${hours}`);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load ledger');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  const loadFunnel = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ days: String(funnelDays) });
      if (funnelLogType !== '*') qs.set('log_type', funnelLogType);
      const r = await api.get(`/admin/announce/funnel?${qs.toString()}`);
      setFunnel(r.data);
    } catch (err) {
      // Funnel is best-effort; don't toast.
      setFunnel(null);
    }
  }, [funnelDays, funnelLogType]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadFunnel(); }, [loadFunnel]);

  const openRevert = (row) => {
    setRevertTarget(row);
    setRevertReason('');
    setRevertOpen(true);
  };

  const confirmRevert = async () => {
    if (!revertTarget) return;
    setReverting(true);
    try {
      await api.post('/admin/autonomous/revert', {
        source: revertTarget.source,
        id: revertTarget.id,
        reason: revertReason.trim() || undefined,
      });
      toast.success('Reverted — audit trail recorded');
      setRevertOpen(false);
      setRevertTarget(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revert failed');
    } finally {
      setReverting(false);
    }
  };

  const summary = data?.summary || {};
  const rows = data?.rows || [];

  const formatTs = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-7xl mx-auto" data-testid="phase178-ledger-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="font-bold text-xl sm:text-2xl text-slate-900">Autonomous Decision Health</h1>
              <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[9px] font-bold uppercase tracking-widest">Phase 178</Badge>
            </div>
            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
              Unified audit ledger across Phase 167 digests, Phase 169 Auto-Pilot fires, and
              Phase 170 external-signal dispatches. Every decision is reversible.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(hours)} onValueChange={(v) => setHours(Number(v))}>
            <SelectTrigger className="h-8 w-[120px] text-xs" data-testid="phase178-window-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1 h</SelectItem>
              <SelectItem value="6">Last 6 h</SelectItem>
              <SelectItem value="24">Last 24 h</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="h-8 text-xs"
            data-testid="phase178-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
            Refresh
          </Button>
        </div>
      </div>

      {/* Roll-up tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
        data-testid="phase178-summary-tiles">
        <SummaryTile label="Decisions" value={summary.rows_returned ?? 0} icon={Activity} accent="violet" />
        <SummaryTile label="Recipients" value={summary.total_recipients ?? 0} icon={Send} accent="emerald" />
        <SummaryTile label="LLM Drafts" value={summary.llm_drafts ?? 0} icon={Sparkles} accent="purple" />
        <SummaryTile label="Fallback Drafts" value={summary.fallback_drafts ?? 0} icon={FileText} accent="amber" />
        <SummaryTile label="Reverted" value={summary.reverted ?? 0} icon={Undo2} accent="rose" />
        <SummaryTile label="Opened+Clicked" value={(summary.opened ?? 0) + (summary.clicked ?? 0)} icon={MailOpen} accent="blue" />
      </div>

      {/* Phase 168.e Funnel chart */}
      <Card data-testid="phase168e-funnel-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <MousePointerClick className="h-4 w-4 text-violet-600" />
                Email Performance Funnel
              </CardTitle>
              <CardDescription className="text-xs">
                Send → delivered → opened → clicked → landed conversion across the selected window.
                Powered by Phase 168 Resend webhook + Phase 168.d UTM landing telemetry.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={funnelLogType} onValueChange={setFunnelLogType}>
                <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="phase168e-logtype">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="*">All email types</SelectItem>
                  <SelectItem value="phase167_weekly_digest">Weekly digests only</SelectItem>
                  <SelectItem value="academy_credentials">Credentials</SelectItem>
                  <SelectItem value="welcome_email">Welcome emails</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(funnelDays)} onValueChange={(v) => setFunnelDays(Number(v))}>
                <SelectTrigger className="h-8 w-[100px] text-xs" data-testid="phase168e-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {funnel ? (
            <FunnelChart stages={funnel.stages} bounceRate={funnel.bounce_rate} />
          ) : (
            <p className="text-xs text-slate-400">No data for this window yet.</p>
          )}
        </CardContent>
      </Card>

      {/* Ledger table */}
      <Card data-testid="phase178-ledger-table">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Decision Ledger</CardTitle>
          <CardDescription className="text-xs">
            Sorted newest-first. {summary.by_source?.phase167_digest || 0} digests · {summary.by_source?.phase169_autopilot || 0} Auto-Pilot · {summary.by_source?.phase170_signal || 0} signals.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-2 sm:px-4">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading ledger…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">
              No autonomous decisions in this window. The platform is quiet right now.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-bold">Source</th>
                    <th className="text-left py-2 px-2 font-bold">When</th>
                    <th className="text-left py-2 px-2 font-bold">Campaign / Tag</th>
                    <th className="text-left py-2 px-2 font-bold">Rule / Template</th>
                    <th className="text-left py-2 px-2 font-bold">Draft</th>
                    <th className="text-right py-2 px-2 font-bold">Recipients</th>
                    <th className="text-left py-2 px-2 font-bold">Telemetry</th>
                    <th className="text-left py-2 px-2 font-bold">Status</th>
                    <th className="text-right py-2 px-2 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const meta = SOURCE_META[row.source] || SOURCE_META.phase167_digest;
                    const Icon = meta.icon;
                    const t = row.telemetry || {};
                    return (
                      <tr
                        key={`${row.source}-${row.id}`}
                        className={`border-b border-slate-100 hover:bg-slate-50/60 ${row.is_reverted ? 'opacity-60' : ''}`}
                        data-testid={`phase178-row-${row.source}-${row.id}`}
                      >
                        <td className="py-2 px-2">
                          <Badge variant="outline" className={`${meta.color} text-[9px] py-0`}>
                            <Icon className="h-2.5 w-2.5 mr-1" />
                            {meta.label.split('·').pop().trim()}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-slate-500 tabular-nums">{formatTs(row.executed_at)}</td>
                        <td className="py-2 px-2 font-semibold text-slate-800 max-w-[220px] truncate" title={row.campaign_tag}>
                          {row.campaign_tag || '—'}
                          {row.academy_name && (
                            <div className="text-[9px] text-slate-400 truncate">{row.academy_name}</div>
                          )}
                        </td>
                        <td className="py-2 px-2 text-slate-500 max-w-[160px] truncate">{row.rule_or_template || '—'}</td>
                        <td className="py-2 px-2">
                          {row.draft_source === 'llm' && (
                            <Badge variant="outline" className="text-[9px] bg-purple-50 text-purple-700 border-purple-200 py-0">
                              <Sparkles className="h-2.5 w-2.5 mr-0.5" /> LLM
                            </Badge>
                          )}
                          {row.draft_source === 'fallback_template' && (
                            <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 py-0">
                              Fallback
                            </Badge>
                          )}
                          {row.draft_source === 'template+metrics' && (
                            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200 py-0">
                              Template
                            </Badge>
                          )}
                          {!['llm', 'fallback_template', 'template+metrics'].includes(row.draft_source) && (
                            <span className="text-[10px] text-slate-400">{row.draft_source || '—'}</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-right tabular-nums font-bold">{row.recipient_count}</td>
                        <td className="py-2 px-2">
                          <div className="flex items-center gap-1.5 text-[10px]">
                            <span className={t.delivered ? 'text-emerald-700 font-bold' : 'text-slate-300'} title="delivered">D{t.delivered || 0}</span>
                            <span className={t.opened ? 'text-blue-700 font-bold' : 'text-slate-300'} title="opened">O{t.opened || 0}</span>
                            <span className={t.clicked ? 'text-violet-700 font-bold' : 'text-slate-300'} title="clicked">C{t.clicked || 0}</span>
                            <span className={t.landed ? 'text-amber-700 font-bold' : 'text-slate-300'} title="landed">L{t.landed || 0}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2">
                          <Badge variant="outline" className={`${STATUS_PILL[row.is_reverted ? 'reverted' : row.status] || STATUS_PILL.sent} text-[9px] py-0`}>
                            {row.is_reverted ? 'reverted' : row.status}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-right">
                          {!row.is_reverted && (
                            <Button
                              size="sm" variant="outline"
                              className="h-6 text-[10px] hover:bg-rose-50 hover:border-rose-300"
                              onClick={() => openRevert(row)}
                              data-testid={`phase178-revert-${row.source}-${row.id}`}
                            >
                              <Undo2 className="h-2.5 w-2.5 mr-0.5" /> Revert
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revert confirmation modal */}
      <Dialog open={revertOpen} onOpenChange={setRevertOpen}>
        <DialogContent className="max-w-md" data-testid="phase178-revert-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <Undo2 className="h-5 w-5" /> Revert this decision
            </DialogTitle>
            <DialogDescription>
              {revertTarget?.source === 'phase167_digest' ? (
                <>The digest email was already delivered — the revert flag records the intent in
                  the audit trail but doesn't un-send. The footer of the email will mark this
                  digest as cancelled on the next live render.</>
              ) : (
                <>This will <strong>retract every in-app notification</strong> generated by this
                  decision. Recipients who haven't opened the alert yet will no longer see it.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-[11px] space-y-1">
              <div><strong>Source:</strong> {SOURCE_META[revertTarget?.source]?.label}</div>
              <div><strong>Campaign:</strong> {revertTarget?.campaign_tag}</div>
              <div><strong>Recipients impacted:</strong> {revertTarget?.recipient_count}</div>
              <div><strong>Fired:</strong> {formatTs(revertTarget?.executed_at)}</div>
            </div>
            <div>
              <Label htmlFor="phase178-reason" className="text-xs font-semibold">Reason (recorded in audit)</Label>
              <Input
                id="phase178-reason"
                placeholder="Optional — e.g. duplicate fire from IMD scraper retry"
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                className="mt-1"
                data-testid="phase178-revert-reason"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setRevertOpen(false)} disabled={reverting}>
                Cancel
              </Button>
              <Button
                onClick={confirmRevert} disabled={reverting}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="phase178-revert-confirm"
              >
                {reverting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Undo2 className="h-3.5 w-3.5 mr-1" />}
                Confirm revert
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon, accent }) {
  const accents = {
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    purple:  { bg: 'bg-purple-50',  text: 'text-purple-700',  border: 'border-purple-200' },
    amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
    rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  }[accent] || { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' };
  return (
    <div className={`${accents.bg} border ${accents.border} rounded-xl p-3`}>
      <div className={`text-[10px] font-bold uppercase tracking-widest ${accents.text} flex items-center gap-1`}>
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-2xl font-extrabold text-slate-900 mt-1 tabular-nums">
        {(value || 0).toLocaleString('en-IN')}
      </div>
    </div>
  );
}
